import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useStrapiStore } from '../stores/strapi'

interface SEOData {
  title?: string | null
  description?: string | null
  image?: {
    url?: string | null
    alternativeText?: string | null
  } | null
  seo?: {
    seoTitle?: string | null
    seoDescription?: string | null
    canonicalUrl?: string | null
    noindex?: boolean | null
    nofollow?: boolean | null
    structuredData?: any | null
  } | null
  socials?: {
    ogTitle?: string | null
    ogDescription?: string | null
    ogImage?: {
      url?: string | null
      alternativeText?: string | null
    } | null
    twitterCard?: 'summary' | 'summary_large_image' | null
    twitterTitle?: string | null
    twitterDescription?: string | null
    twitterImage?: {
      url?: string | null
      alternativeText?: string | null
    } | null
  } | null
}

// Global reactive SEO state
const currentSEO = ref<SEOData>({})

export const useSEO = () => {
  const route = useRoute()
  const strapiStore = useStrapiStore()

  // Set SEO data for current view
  const setSEO = (seoData: SEOData) => {
    currentSEO.value = { ...seoData }
  }

  // Clear SEO data (useful for route changes)
  const clearSEO = () => {
    currentSEO.value = {}
  }

  // Computed values for the meta components
  const seoData = computed(() => ({
    title: currentSEO.value.title,
    description: currentSEO.value.description,
    image: currentSEO.value.image,
    seo: currentSEO.value.seo,
    socials: currentSEO.value.socials
  }))

  // Helper for common page setup
  const setPageSEO = (pageData: any) => {
    setSEO({
      title: pageData?.title,
      description: pageData?.description,
      image: pageData?.featuredImage?.asset,
      seo: pageData?.seo,
      socials: pageData?.socials
    })
  }

  // Helper for post/article setup
  const setPostSEO = (postData: any) => {
    setSEO({
      title: postData?.title,
      description: postData?.description,
      image: postData?.featuredImage?.asset || postData?.thumbnail,
      seo: postData?.seo,
      socials: postData?.socials
    })
  }

  // Helper for setting simple title-only SEO (like error pages)
  const setSimpleSEO = (title: string, description?: string) => {
    setSEO({
      title,
      description,
      image: null,
      seo: strapiStore.globalSeo,
      socials: strapiStore.globalSocials
    })
  }

  return {
    // State
    seoData,
    
    // Methods
    setSEO,
    clearSEO,
    setPageSEO,
    setPostSEO,
    setSimpleSEO
  }
}

// Export the reactive state for App.vue to consume
export const getCurrentSEO = () => currentSEO 